import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminListPrograms, adminCreateProgram, adminUpdateProgram, adminDeleteProgram } from "@/lib/apiClient";
import { Switch } from "@/components/ui/switch";
import { Plus, Save, Trash2, X } from "lucide-react";

const EMPTY = {
  id: "", title: "", tagline: "", description: "", price: 0,
  currency: "USD", is_free: true, image: "", enrollment_url: "",
  category: "Comprehension", order: 99, is_in_app: false, enabled: true,
};

export default function AdminPrograms() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => setRows(await adminListPrograms());
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (rows.some((r) => r.id === editing.id && editing._new)) {
        toast.error("Program id already exists"); return;
      }
      const body = { ...editing }; delete body._new;
      if (editing._new) await adminCreateProgram(body);
      else await adminUpdateProgram(body.id, body);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm(`Delete program ${id}?`)) return;
    await adminDeleteProgram(id);
    toast.success("Deleted"); load();
  };

  const toggleEnabled = async (p) => {
    const enabled = !(p.enabled ?? true);
    setRows((prev) => prev.map((r) => (r.id === p.id ? { ...r, enabled } : r)));
    try {
      await adminUpdateProgram(p.id, { ...p, enabled });
    } catch (e) {
      setRows((prev) => prev.map((r) => (r.id === p.id ? { ...r, enabled: !enabled } : r)));
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div data-testid="admin-programs">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#1F2937]">Programs (CMS)</h1>
        <button data-testid="add-program-btn" onClick={() => setEditing({ ...EMPTY, _new: true })}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-sm font-medium transition">
          <Plus className="w-4 h-4" /> New program
        </button>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((p) => (
          <div key={p.id} className="rounded-2xl bg-white border border-[#E7E0D2] overflow-hidden" data-testid={`prog-${p.id}`}>
            {p.image && <img src={p.image} alt={p.title} className="w-full aspect-[16/10] object-cover" />}
            <div className="p-4">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[#4B5563]">
                <span>{p.category}</span>
                <span className={p.is_free ? "text-[#10B981]" : "text-[#D97706] font-medium"}>{p.is_free ? "FREE" : `$${p.price}`}</span>
              </div>
              <div className="font-heading text-base mt-2 text-[#1F2937]">{p.title}</div>
              <div className="text-xs text-[#4B5563] mt-1 line-clamp-2">{p.tagline}</div>
              <div className="mt-3 flex items-center gap-2">
                <Switch
                  checked={p.enabled ?? true}
                  onCheckedChange={() => toggleEnabled(p)}
                  data-testid={`prog-enabled-${p.id}`}
                />
                <span className={`text-xs font-medium ${(p.enabled ?? true) ? "text-[#10B981]" : "text-[#6B7280]"}`}>
                  {(p.enabled ?? true) ? "Enabled" : "Disabled"}
                </span>
              </div>
              <div className="mt-2 flex gap-2">
                <button onClick={() => setEditing({ ...p })}
                  className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937]">Edit</button>
                <button onClick={() => del(p.id)}
                  className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#EF4444] text-[#EF4444] hover:bg-[#FEF2F2] inline-flex items-center gap-1">
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center px-4 py-8" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-3xl border border-[#E7E0D2] w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="prog-edit-modal">
            <div className="flex items-center justify-between p-5 border-b border-[#E7E0D2]">
              <div className="font-heading text-lg">{editing._new ? "New program" : `Edit · ${editing.title}`}</div>
              <button onClick={() => setEditing(null)} className="w-8 h-8 rounded-full grid place-items-center hover:bg-[#FDFBF7]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <Field label="ID (slug)" disabled={!editing._new} value={editing.id} onChange={(v) => setEditing({ ...editing, id: v })} />
              <Field label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
              <Field label="Tagline" value={editing.tagline} onChange={(v) => setEditing({ ...editing, tagline: v })} />
              <Field label="Description" textarea value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} />
              <div className="grid grid-cols-3 gap-3">
                <Field label="Price" type="number" value={editing.price} onChange={(v) => setEditing({ ...editing, price: Number(v) || 0 })} />
                <Field label="Currency" value={editing.currency} onChange={(v) => setEditing({ ...editing, currency: v })} />
                <Field label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} />
              </div>
              <Field label="Image URL" value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} />
              {editing.image && (
                <div className="rounded-xl overflow-hidden border border-[#E7E0D2] bg-[#FDFBF7]">
                  <img src={editing.image} alt="" className="w-full max-h-40 object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                </div>
              )}
              <Field label="Enrollment URL" value={editing.enrollment_url} onChange={(v) => setEditing({ ...editing, enrollment_url: v })} />
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_free} onChange={(e) => setEditing({ ...editing, is_free: e.target.checked })} /> Free</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_in_app} onChange={(e) => setEditing({ ...editing, is_in_app: e.target.checked })} /> In-app program</label>
                <Field label="Order" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: Number(v) || 0 })} small />
              </div>
            </div>
            <div className="p-5 border-t border-[#E7E0D2] flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-sm">Cancel</button>
              <button data-testid="prog-save" onClick={save} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-sm font-medium">
                <Save className="w-4 h-4" /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", textarea, disabled, small }) {
  return (
    <label className={`block ${small ? "" : ""}`}>
      <div className="text-[11px] uppercase tracking-widest text-[#4B5563] mb-1">{label}</div>
      {textarea ? (
        <textarea rows={3} disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] px-3 py-2 text-sm outline-none disabled:opacity-50" />
      ) : (
        <input type={type} disabled={disabled} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] px-3 py-2 text-sm outline-none disabled:opacity-50" />
      )}
    </label>
  );
}
